import { website } from '@getpopsure/private-constants';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '@getpopsure/qnr-framework';
import * as Sentry from '@sentry/react';
import { ErrorWithAction } from 'components/ErrorWithAction';
import { BaseQuestionnaire } from 'features/generalQuestionnaire/generalPolicy/actions';
import hashSum from 'hash-sum';
import { Policy } from 'models/policies';
import { useEffect, useState } from 'react';
import { useSafeTranslation } from 'shared/i18n';

import { useGetQuotes } from '../../hooks/useGetQuotes';
import {
  AdjustPolicyConfig,
  AdjustPolicyReturnValue,
  BaseQuote,
} from '../../models';
import { AdjustPolicyView } from './AdjustPolicy.view';

export const AdjustPolicy = <Questionnaire extends QuestionnaireQuestions>(
  props: QuestionnaireFormProps<Questionnaire, AdjustPolicyReturnValue> &
    AdjustPolicyConfig & {
      configuration: {
        policy: Policy;
        policyQuestionnaire: BaseQuestionnaire;
        policyQuote: BaseQuote;
      };
    }
) => {
  const { t } = useSafeTranslation();

  const {
    questionnaireAnswers,
    featureName: featureNameFromProps,
    configuration: { policyQuestionnaire, policyQuote },
    onSubmitValue,
  } = props;

  const featureName =
    featureNameFromProps !== '1' ? featureNameFromProps : 'LIABILITY';

  const flattenedOriginalAnswers = {
    ...policyQuestionnaire?.answers.personalInfo,
    ...policyQuestionnaire?.answers.tariffInfo,
  };

  const [optionValues, setOptionValues] =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useState<Record<string, any>>(questionnaireAnswers);

  const reset = () => setOptionValues(flattenedOriginalAnswers);

  const {
    quotes,
    isError: isQuotesError,
    error: quotesError,
    isInitialLoading: isInitialQuotesLoading,
  } = useGetQuotes({
    featureName,
    answers: optionValues,
  });

  if (isQuotesError) {
    Sentry.captureException(quotesError, {
      tags: {
        feature: 'ADJUST_POLICY',
      },
      extra: {
        description: `Error happened while generating new quotes for vertical: ${featureName}`,
      },
    });
  }

  useEffect(() => {
    if (
      Object.entries(optionValues).length === 0 &&
      Object.entries(flattenedOriginalAnswers).length > 0
    ) {
      setOptionValues(flattenedOriginalAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedOriginalAnswers]);

  if (!isInitialQuotesLoading && !quotes) {
    return (
      <ErrorWithAction
        title={t('adjustPolicy.fetchingError.title', 'Something went wrong')}
        description={t(
          'adjustPolicy.fetchingError.description',
          'Sorry, there was a problem loading your policy. If the problem persists after starting again, please contact our support team.'
        )}
        cta={{
          title: t('components.quote.error.cta', 'Contact support'),
          onClick: () => {
            window.location.href = website.support;
            return null;
          },
        }}
      />
    );
  }

  const hasChangedOptions =
    hashSum(flattenedOriginalAnswers) !== hashSum(optionValues);

  const handleSubmit = () => {
    onSubmitValue(
      {
        reviewMonthlyPrice: quotes?.[0].price,
        questionnaireTransform: {
          personalInfo: Object.keys(
            policyQuestionnaire?.answers?.personalInfo ?? {}
          ),
          tariffInfo: Object.keys(
            policyQuestionnaire?.answers?.tariffInfo ?? {}
          ),
        },
      },
      optionValues as Partial<Questionnaire>
    );
  };

  return (
    <AdjustPolicyView
      handleSubmit={handleSubmit}
      setOptionValues={setOptionValues}
      optionValues={optionValues}
      newPrice={quotes?.[0].price || 0}
      oldPrice={policyQuote?.price || 0}
      reset={reset}
      hasChangedOptions={hasChangedOptions}
      {...props}
    />
  );
};
